// src/components/Register.js
import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { auth } from '../services/api';
import heroBackground from '../assets/hero-background.webp';
import logo from '../assets/logo.png';

const validatePassword = (password) => {
  if (!password) {
    return 'Password is required';
  }
  const errors = [];
  if (password.length < 8) {
    errors.push('• At least 8 characters');
  }
  if (!/[a-z]/.test(password)) {
    errors.push('• At least 1 lowercase letter');
  }
  if (!/[A-Z]/.test(password)) {
    errors.push('• At least 1 uppercase letter');
  }
  if (!/[0-9]/.test(password)) {
    errors.push('• At least 1 number');
  }
  // Symbol: any non-alphanumeric character
  if (!/[^a-zA-Z0-9]/.test(password)) {
    errors.push('• At least 1 symbol');
  }
  if (errors.length > 0) {
    return 'Password must have:\n' + errors.join('\n');
  }
  return null;
};

function Register({ setAuth }) {
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [companyName, setCompanyName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const handleChange = (e) => {
    const { name, value } = e.target;
    switch(name) {
        case 'firstName': setFirstName(value); break;
        case 'lastName': setLastName(value); break;
        case 'companyName': setCompanyName(value); break;
        case 'email': setEmail(value); break;
        case 'password': setPassword(value); break;
        case 'confirmPassword': setConfirmPassword(value); break;
        default: break;
    }
    
    if (errors[name]) {
        setErrors(prev => {
            const newErrors = { ...prev };
            delete newErrors[name];
            return newErrors;
        });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    let formErrors = {};

    if (!firstName.trim()) {
      formErrors.firstName = 'First name is required';
    }

    if (!lastName.trim()) {
      formErrors.lastName = 'Last name is required';
    }

    if (companyName && companyName.trim().length > 0 && companyName.trim().length < 2) {
      formErrors.companyName = 'Company name must be at least 2 characters';
    }

    if (!/^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/.test(email)) {
        formErrors.email = 'Invalid email address';
    }
    
    const passwordError = validatePassword(password);
    if (passwordError) {
      formErrors.password = passwordError;
    }
    
    if (password !== confirmPassword) {
      formErrors.confirmPassword = 'Passwords do not match';
    }


    if (Object.keys(formErrors).length > 0) {
      setErrors(formErrors);
      return;
    }

    setLoading(true);

    try {
      const response = await auth.register({
        firstName: firstName.trim(),
        lastName: lastName.trim(),
        companyName: companyName.trim() || null,
        email,
        password
      });
      localStorage.setItem('token', response.data.token);
      localStorage.setItem('user', JSON.stringify(response.data.user));
      setAuth(true);
      navigate('/dashboard');
    } catch (err) {
      setErrors({ api: err.response?.data?.error || 'Registration failed. Please try again.' });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{
      minHeight: '100vh',
      position: 'relative',
      display: 'flex',
      flexDirection: 'column',
      padding: '10px'
    }}>
      {/* Navigation Bar with Logo */}
      <nav style={{
        position: 'relative',
        zIndex: '2',
        maxWidth: '1200px',
        width: '100%',
        margin: '0 auto 20px auto',
      }}>
        <div style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between'
        }}>
          <button
            onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
            style={{
              background: 'none',
              border: 'none',
              padding: 0,
              cursor: 'pointer',
              transition: 'opacity 0.2s'
            }}
            onMouseEnter={(e) => e.target.style.opacity = '0.8'}
            onMouseLeave={(e) => e.target.style.opacity = '1'}
            aria-label="Go to top of page"
          >
            <img
              src={logo}
              alt="Logo"
              style={{
                width: '240px',
                height: 'auto',
                objectFit: 'contain',
                display: 'block'
              }}
            />
          </button>
        </div>
      </nav>

      {/* Background Image */}
      <div style={{
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        zIndex: '0',
        overflow: 'hidden'
      }}>
        <img
          src={heroBackground}
          alt="Background"
          style={{
            width: '100%',
            height: '100%',
            objectFit: 'cover',
            display: 'block'
          }}
        />
        {/* Gradient Overlay */}
        <div style={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'linear-gradient(to right bottom, rgba(249, 250, 251, 0.9), rgba(249, 250, 251, 0.7), rgba(249, 250, 251, 0.9))'
        }}></div>
      </div>

      {/* Main Content Container */}
      <div style={{
        flex: 1,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        position: 'relative',
        zIndex: '1'
      }}>
        {/* Register Form Container */}
        <div style={{
          position: 'relative',
          zIndex: '1',
          background: 'rgba(255, 255, 255, 0.95)',
          backdropFilter: 'blur(20px) saturate(180%)',
          borderRadius: '32px',
          boxShadow: '0 32px 64px -12px rgba(0, 0, 0, 0.25), 0 0 0 1px rgba(255, 255, 255, 0.5) inset, 0 1px 2px rgba(0, 0, 0, 0.05)',
          padding: '48px 56px',
          width: '100%',
          maxWidth: '680px',
          border: '1px solid rgba(255, 255, 255, 0.2)'
        }}>
          {/* Header Section */}
          <div style={{ textAlign: 'center', marginBottom: '28px' }}>
            <div style={{
              width: '60px',
              height: '60px',
              borderRadius: '16px',
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              margin: '0 auto 16px auto',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              boxShadow: '0 12px 32px rgba(102, 126, 234, 0.35), 0 0 0 1px rgba(255, 255, 255, 0.1) inset',
              position: 'relative'
            }}>
              <div style={{
                position: 'absolute',
                inset: '-2px',
                borderRadius: '16px',
                background: 'linear-gradient(135deg, #667eea, #764ba2)',
                opacity: 0.3,
                filter: 'blur(8px)',
                zIndex: -1
              }}></div>
              <svg width="30" height="30" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2"></path>
                <circle cx="9" cy="7" r="4"></circle>
                <path d="M22 21v-2a4 4 0 0 0-3-3.87"></path>
                <path d="M16 3.13a4 4 0 0 1 0 7.75"></path>
              </svg>
            </div>
            <h1 style={{
              fontSize: '26px',
              fontWeight: '700',
              marginBottom: '6px',
              color: '#0f172a',
              letterSpacing: '-0.5px',
              lineHeight: '1.2',
              background: 'linear-gradient(135deg, #0f172a 0%, #1e293b 100%)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              backgroundClip: 'text'
            }}>
              Start Your AI Chatbot Journey
            </h1>
            <p style={{
              color: '#64748b',
              fontSize: '14px',
              lineHeight: '1.4',
              fontWeight: '400',
              letterSpacing: '-0.1px'
            }}>
              Create an account to get started.
            </p>
          </div>

          {errors.api && (
            <div style={{
              background: 'linear-gradient(135deg, rgba(254, 226, 226, 0.95) 0%, rgba(254, 202, 202, 0.95) 100%)',
              border: '1.5px solid #f87171',
              color: '#991b1b',
              padding: '12px 16px',
              borderRadius: '12px',
              marginBottom: '18px',
              fontSize: '13px',
              display: 'flex',
              alignItems: 'center',
              gap: '10px',
              boxShadow: '0 4px 16px rgba(239, 68, 68, 0.2), 0 0 0 1px rgba(248, 113, 113, 0.1) inset',
              backdropFilter: 'blur(8px)'
            }}>
              <div style={{
                width: '20px',
                height: '20px',
                borderRadius: '50%',
                background: 'rgba(239, 68, 68, 0.15)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0
              }}>
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                  <circle cx="12" cy="12" r="10"></circle>
                  <line x1="12" y1="8" x2="12" y2="12"></line>
                  <line x1="12" y1="16" x2="12.01" y2="16"></line>
                </svg>
              </div>
              <span style={{ fontWeight: '500', lineHeight: '1.4' }}>{errors.api}</span>
            </div>
          )}

          <form onSubmit={handleSubmit} noValidate>
            {/* Name Fields - Two Column Layout */}
            <div style={{
              display: 'grid',
              gridTemplateColumns: '1fr 1fr',
              gap: '14px',
              marginBottom: '16px'
            }}>
              <div>
                <label style={{
                  display: 'block',
                  fontSize: '11px',
                  fontWeight: '600',
                  color: '#1e293b',
                  marginBottom: '7px',
                  letterSpacing: '0.2px',
                  textTransform: 'uppercase'
                }}>
                  First Name
                </label>
                <div style={{ position: 'relative' }}>
                  <input
                    type="text"
                    name="firstName"
                    value={firstName}
                    onChange={handleChange}
                    required
                    placeholder="John"
                    style={{
                      width: '100%',
                      padding: '12px 14px',
                      border: `1.5px solid ${errors.firstName ? '#f87171' : '#e2e8f0'}`,
                      borderRadius: '12px',
                      fontSize: '14px',
                      outline: 'none',
                      transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                      boxSizing: 'border-box',
                      background: 'rgba(255, 255, 255, 0.8)',
                      color: '#0f172a',
                      fontWeight: '500',
                      letterSpacing: '-0.1px'
                    }}
                    onFocus={(e) => {
                      e.target.style.borderColor = '#667eea';
                      e.target.style.boxShadow = '0 0 0 4px rgba(102, 126, 234, 0.08), 0 4px 12px rgba(102, 126, 234, 0.15)';
                      e.target.style.background = '#ffffff';
                      e.target.style.transform = 'translateY(-1px)';
                    }}
                    onBlur={(e) => {
                      e.target.style.borderColor = errors.firstName ? '#f87171' : '#e2e8f0';
                      e.target.style.boxShadow = 'none';
                      e.target.style.background = 'rgba(255, 255, 255, 0.8)';
                      e.target.style.transform = 'translateY(0)';
                    }}
                  />
                </div>
                {errors.firstName && <p style={{ color: '#ef4444', fontSize: '12px', marginTop: '4px' }}>{errors.firstName}</p>}
              </div>

              <div>
                <label style={{
                  display: 'block',
                  fontSize: '11px',
                  fontWeight: '600',
                  color: '#1e293b',
                  marginBottom: '7px',
                  letterSpacing: '0.2px',
                  textTransform: 'uppercase'
                }}>
                  Last Name
                </label>
                <div style={{ position: 'relative' }}>
                  <input
                    type="text"
                    name="lastName"
                    value={lastName}
                    onChange={handleChange}
                    required
                    placeholder="Doe"
                    style={{
                      width: '100%',
                      padding: '12px 14px',
                      border: `1.5px solid ${errors.lastName ? '#f87171' : '#e2e8f0'}`,
                      borderRadius: '12px',
                      fontSize: '14px',
                      outline: 'none',
                      transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                      boxSizing: 'border-box',
                      background: 'rgba(255, 255, 255, 0.8)',
                      color: '#0f172a',
                      fontWeight: '500',
                      letterSpacing: '-0.1px'
                    }}
                    onFocus={(e) => {
                      e.target.style.borderColor = '#667eea';
                      e.target.style.boxShadow = '0 0 0 4px rgba(102, 126, 234, 0.08), 0 4px 12px rgba(102, 126, 234, 0.15)';
                      e.target.style.background = '#ffffff';
                      e.target.style.transform = 'translateY(-1px)';
                    }}
                    onBlur={(e) => {
                      e.target.style.borderColor = errors.lastName ? '#f87171' : '#e2e8f0';
                      e.target.style.boxShadow = 'none';
                      e.target.style.background = 'rgba(255, 255, 255, 0.8)';
                      e.target.style.transform = 'translateY(0)';
                    }}
                  />
                </div>
                 {errors.lastName && <p style={{ color: '#ef4444', fontSize: '12px', marginTop: '4px' }}>{errors.lastName}</p>}
              </div>
            </div>

            <div style={{ marginBottom: '16px' }}>
                <label style={{
                  display: 'block',
                  fontSize: '11px',
                  fontWeight: '600',
                  color: '#1e293b',
                  marginBottom: '7px',
                  letterSpacing: '0.2px',
                  textTransform: 'uppercase'
                }}>
                  Company Name <span style={{ color: '#94a3b8', fontWeight: '400', textTransform: 'none' }}>(Optional)</span>
                </label>
                <div style={{ position: 'relative' }}>
                  <input
                    type="text"
                    name="companyName"
                    value={companyName}
                    onChange={handleChange}
                    placeholder="Acme Inc."
                    style={{
                      width: '100%',
                      padding: '12px 14px',
                      border: `1.5px solid ${errors.companyName ? '#f87171' : '#e2e8f0'}`,
                      borderRadius: '12px',
                      fontSize: '14px',
                      outline: 'none',
                      transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                      boxSizing: 'border-box',
                      background: 'rgba(255, 255, 255, 0.8)',
                      color: '#0f172a',
                      fontWeight: '500',
                      letterSpacing: '-0.1px'
                    }}
                    onFocus={(e) => {
                      e.target.style.borderColor = '#667eea';
                      e.target.style.boxShadow = '0 0 0 4px rgba(102, 126, 234, 0.08), 0 4px 12px rgba(102, 126, 234, 0.15)';
                      e.target.style.background = '#ffffff';
                      e.target.style.transform = 'translateY(-1px)';
                    }}
                    onBlur={(e) => {
                      e.target.style.borderColor = errors.companyName ? '#f87171' : '#e2e8f0';
                      e.target.style.boxShadow = 'none';
                      e.target.style.background = 'rgba(255, 255, 255, 0.8)';
                      e.target.style.transform = 'translateY(0)';
                    }}
                  />
                </div>
                {errors.companyName && <p style={{ color: '#ef4444', fontSize: '12px', marginTop: '4px' }}>{errors.companyName}</p>}
              </div>

            <div style={{ marginBottom: '16px' }}>
                <label style={{
                  display: 'block',
                  fontSize: '11px',
                  fontWeight: '600',
                  color: '#1e293b',
                  marginBottom: '7px',
                  letterSpacing: '0.2px',
                  textTransform: 'uppercase'
                }}>
                  Email Address
                </label>
                <div style={{ position: 'relative' }}>
                  <input
                    type="email"
                    name="email"
                    value={email}
                    onChange={handleChange}
                    required
                    placeholder="you@example.com"
                    style={{
                      width: '100%',
                      padding: '12px 14px',
                      border: `1.5px solid ${errors.email ? '#f87171' : '#e2e8f0'}`,
                      borderRadius: '12px',
                      fontSize: '14px',
                      outline: 'none',
                      transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                      boxSizing: 'border-box',
                      background: 'rgba(255, 255, 255, 0.8)',
                      color: '#0f172a',
                      fontWeight: '500',
                      letterSpacing: '-0.1px'
                    }}
                    onFocus={(e) => {
                      e.target.style.borderColor = '#667eea';
                      e.target.style.boxShadow = '0 0 0 4px rgba(102, 126, 234, 0.08), 0 4px 12px rgba(102, 126, 234, 0.15)';
                      e.target.style.background = '#ffffff';
                      e.target.style.transform = 'translateY(-1px)';
                    }}
                    onBlur={(e) => {
                      e.target.style.borderColor = errors.email ? '#f87171' : '#e2e8f0';
                      e.target.style.boxShadow = 'none';
                      e.target.style.background = 'rgba(255, 255, 255, 0.8)';
                      e.target.style.transform = 'translateY(0)';
                    }}
                  />
                </div>
                {errors.email && <p style={{ color: '#ef4444', fontSize: '12px', marginTop: '4px' }}>{errors.email}</p>}
              </div>

            <div style={{
              display: 'grid',
              gridTemplateColumns: '1fr 1fr',
              gap: '14px',
              marginBottom: '20px'
            }}>
              <div>
                <label style={{
                  display: 'block',
                  fontSize: '11px',
                  fontWeight: '600',
                  color: '#1e293b',
                  marginBottom: '7px',
                  letterSpacing: '0.2px',
                  textTransform: 'uppercase'
                }}>
                  Password
                </label>
                <div style={{ position: 'relative' }}>
                  <input
                    type={showPassword ? 'text' : 'password'}
                    name="password"
                    value={password}
                    onChange={handleChange}
                    required
                    placeholder="Create a strong password"
                    minLength={8}
                    style={{
                      width: '100%',
                      padding: '12px 46px 12px 14px',
                      border: `1.5px solid ${errors.password ? '#f87171' : '#e2e8f0'}`,
                      borderRadius: '12px',
                      fontSize: '14px',
                      outline: 'none',
                      transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                      boxSizing: 'border-box',
                      background: 'rgba(255, 255, 255, 0.8)',
                      color: '#0f172a',
                      fontWeight: '500',
                      letterSpacing: '-0.1px'
                    }}
                    onFocus={(e) => {
                      e.target.style.borderColor = '#667eea';
                      e.target.style.boxShadow = '0 0 0 4px rgba(102, 126, 234, 0.08), 0 4px 12px rgba(102, 126, 234, 0.15)';
                      e.target.style.background = '#ffffff';
                      e.target.style.transform = 'translateY(-1px)';
                    }}
                    onBlur={(e) => {
                      e.target.style.borderColor = errors.password ? '#f87171' : '#e2e8f0';
                      e.target.style.boxShadow = 'none';
                      e.target.style.background = 'rgba(255, 255, 255, 0.8)';
                      e.target.style.transform = 'translateY(0)';
                    }}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    style={{
                      position: 'absolute',
                      right: '14px',
                      top: '50%',
                      transform: 'translateY(-50%)',
                      background: 'none',
                      border: 'none',
                      cursor: 'pointer',
                      padding: '6px',
                      color: '#64748b',
                      display: 'flex',
                      alignItems: 'center',
                      borderRadius: '6px',
                      transition: 'all 0.2s'
                    }}
                    onMouseEnter={(e) => {
                      e.target.style.background = 'rgba(148, 163, 184, 0.1)';
                      e.target.style.color = '#475569';
                    }}
                    onMouseLeave={(e) => {
                      e.target.style.background = 'none';
                      e.target.style.color = '#64748b';
                    }}
                  >
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                      {!showPassword ? (
                        <>
                          <path d="M9.88 9.88a3 3 0 1 0 4.24 4.24" />
                          <path d="M10.73 5.08A10.43 10.43 0 0 1 12 5c7 0 11 8 11 8a13.16 13.16 0 0 1-1.67 2.68" />
                          <path d="M6.61 6.61A13.526 13.526 0 0 0 2 12s3 8 10 8a9.74 9.74 0 0 0 5.39-1.61" />
                          <line x1="2" y1="2" x2="22" y2="22" />
                        </>
                      ) : (
                        <>
                          <path d="M2 12s3-7 10-7 10 7 10 7-3 7-10 7-10-7-10-7Z" />
                          <circle cx="12" cy="12" r="3" />
                        </>
                      )}
                    </svg>
                  </button>
                </div>
                {errors.password ? <p style={{ color: '#ef4444', fontSize: '12px', marginTop: '4px', whiteSpace: 'pre-line' }}>{errors.password}</p> : <div style={{
                  fontSize: '11px',
                  color: '#94a3b8',
                  marginTop: '5px',
                  marginBottom: 0,
                  fontWeight: '400',
                  letterSpacing: '0.1px',
                  lineHeight: '1.5'
                }}>
                  Password requirements:
                  <br />• At least 8 characters
                  <br />• At least 1 lowercase letter
                  <br />• At least 1 uppercase letter
                  <br />• At least 1 number
                  <br />• At least 1 symbol
                </div>}
              </div>

              <div>
                <label style={{
                  display: 'block',
                  fontSize: '11px',
                  fontWeight: '600',
                  color: '#1e293b',
                  marginBottom: '7px',
                  letterSpacing: '0.2px',
                  textTransform: 'uppercase'
                }}>
                  Confirm Password
                </label>
                <div style={{ position: 'relative' }}>
                  <input
                    type={showConfirmPassword ? 'text' : 'password'}
                    name="confirmPassword"
                    value={confirmPassword}
                    onChange={handleChange}
                    required
                    placeholder="Re-enter your password"
                    style={{
                      width: '100%',
                      padding: '12px 46px 12px 14px',
                      border: `1.5px solid ${errors.confirmPassword ? '#f87171' : '#e2e8f0'}`,
                      borderRadius: '12px',
                      fontSize: '14px',
                      outline: 'none',
                      transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                      boxSizing: 'border-box',
                      background: 'rgba(255, 255, 255, 0.8)',
                      color: '#0f172a',
                      fontWeight: '500',
                      letterSpacing: '-0.1px'
                    }}
                    onFocus={(e) => {
                      e.target.style.borderColor = '#667eea';
                      e.target.style.boxShadow = '0 0 0 4px rgba(102, 126, 234, 0.08), 0 4px 12px rgba(102, 126, 234, 0.15)';
                      e.target.style.background = '#ffffff';
                      e.target.style.transform = 'translateY(-1px)';
                    }}
                    onBlur={(e) => {
                      e.target.style.borderColor = errors.confirmPassword ? '#f87171' : '#e2e8f0';
                      e.target.style.boxShadow = 'none';
                      e.target.style.background = 'rgba(255, 255, 255, 0.8)';
                      e.target.style.transform = 'translateY(0)';
                    }}
                  />
                  <button
                    type="button"
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    style={{
                      position: 'absolute',
                      right: '14px',
                      top: '50%',
                      transform: 'translateY(-50%)',
                      background: 'none',
                      border: 'none',
                      cursor: 'pointer',
                      padding: '6px',
                      color: '#64748b',
                      display: 'flex',
                      alignItems: 'center',
                      borderRadius: '6px',
                      transition: 'all 0.2s'
                    }}
                    onMouseEnter={(e) => {
                      e.target.style.background = 'rgba(148, 163, 184, 0.1)';
                      e.target.style.color = '#475569';
                    }}
                    onMouseLeave={(e) => {
                      e.target.style.background = 'none';
                      e.target.style.color = '#64748b';
                    }}
                  >
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                      {!showConfirmPassword ? (
                        <>
                          <path d="M9.88 9.88a3 3 0 1 0 4.24 4.24" />
                          <path d="M10.73 5.08A10.43 10.43 0 0 1 12 5c7 0 11 8 11 8a13.16 13.16 0 0 1-1.67 2.68" />
                          <path d="M6.61 6.61A13.526 13.526 0 0 0 2 12s3 8 10 8a9.74 9.74 0 0 0 5.39-1.61" />
                          <line x1="2" y1="2" x2="22" y2="22" />
                        </>
                      ) : (
                        <>
                          <path d="M2 12s3-7 10-7 10 7 10 7-3 7-10 7-10-7-10-7Z" />
                          <circle cx="12" cy="12" r="3" />
                        </>
                      )}
                    </svg>
                  </button>
                </div>
                {errors.confirmPassword && <p style={{ color: '#ef4444', fontSize: '12px', marginTop: '4px' }}>{errors.confirmPassword}</p>}
              </div>
            </div>

            <button
              type="submit"
              disabled={loading}
              style={{
                width: '100%',
                padding: '14px',
                background: loading
                  ? '#cbd5e1'
                  : 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                color: 'white',
                border: 'none',
                outline: 'none',
                borderRadius: '14px',
                fontSize: '15px',
                fontWeight: '600',
                cursor: loading ? 'not-allowed' : 'pointer',
                transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
                marginBottom: '20px',
                boxShadow: loading
                  ? 'none'
                  : '0 12px 32px rgba(102, 126, 234, 0.35)',
                letterSpacing: '0.2px',
                position: 'relative',
                overflow: 'hidden'
              }}
              onMouseEnter={(e) => {
                if (!loading) {
                  e.target.style.transform = 'translateY(-3px) scale(1.01)';
                  e.target.style.boxShadow = '0 20px 40px rgba(102, 126, 234, 0.45)';
                }
              }}
              onMouseLeave={(e) => {
                if (!loading) {
                  e.target.style.transform = 'translateY(0) scale(1)';
                  e.target.style.boxShadow = '0 12px 32px rgba(102, 126, 234, 0.35)';
                }
              }}
              onFocus={(e) => {
                if (!loading) {
                  e.target.style.transform = 'translateY(-3px) scale(1.01)';
                  e.target.style.boxShadow = '0 20px 40px rgba(102, 126, 234, 0.45)';
                }
              }}
              onBlur={(e) => {
                if (!loading) {
                  e.target.style.transform = 'translateY(0) scale(1)';
                  e.target.style.boxShadow = '0 12px 32px rgba(102, 126, 234, 0.35)';
                }
              }}
            >
              {loading ? (
                <span style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '10px' }}>
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" style={{ animation: 'spin 1s linear infinite' }}>
                    <line x1="12" y1="2" x2="12" y2="6"></line>
                    <line x1="12" y1="18" x2="12" y2="22"></line>
                    <line x1="4.93" y1="4.93" x2="7.76" y2="7.76"></line>
                    <line x1="16.24" y1="16.24" x2="19.07" y2="19.07"></line>
                    <line x1="2" y1="12" x2="6" y2="12"></line>
                    <line x1="18" y1="12" x2="22" y2="12"></line>
                    <line x1="4.93" y1="19.07" x2="7.76" y2="16.24"></line>
                    <line x1="16.24" y1="7.76" x2="19.07" y2="4.93"></line>
                  </svg>
                  Creating account...
                </span>
              ) : (
                <span style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px' }}>
                  Create Account
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M5 12h14"></path>
                    <path d="M12 5l7 7-7 7"></path>
                  </svg>
                </span>
              )}
            </button>

            <div style={{
              textAlign: 'center',
              paddingTop: '20px',
              borderTop: '1px solid rgba(226, 232, 240, 0.8)'
            }}>
              <p style={{
                color: '#64748b',
                fontSize: '13px',
                marginBottom: '10px',
                fontWeight: '400',
                letterSpacing: '0.1px'
              }}>
                Already have an account?
              </p>
              <Link
                to="/login"
                style={{
                  color: '#667eea',
                  textDecoration: 'none',
                  fontWeight: '600',
                  fontSize: '15px',
                  transition: 'all 0.2s',
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '6px',
                  letterSpacing: '-0.2px'
                }}
                onMouseEnter={(e) => {
                  e.target.style.color = '#5568d3';
                  e.target.style.gap = '8px';
                }}
                onMouseLeave={(e) => {
                  e.target.style.color = '#667eea';
                  e.target.style.gap = '6px';
                }}
              >
                Sign in
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M5 12h14"></path>
                  <path d="M12 5l7 7-7 7"></path>
                </svg>
              </Link>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}

export default Register;